import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useRouting } from '../../../utils/useRouting';
import DAC from './DAC';
import Transfer from './Transfer';
import API from '@aws-amplify/api';
import ResetWerte from './ResetDaten';
import Messbericht from './Messbericht';
import SetupHeader from '../SetupHeader';
import Grid from '@material-ui/core/Grid';
import DeleteDevice from './DeleteDevice';
import ResetStunden from './ResetStunden';
import VertragsEnde from './VertragsEnde';
import VertragsArten from './VertragsArten';
import WartungGeplant from './WartungGeplant';
import ServiceGeplant from './ServiceGeplant';
import Container from '@material-ui/core/Container';
import VertragsKommentar from './VertragsKommentar';
import WartungAbgeschlossen from './WartungAbgeschlossen';
import ServiceAbgeschlossen from './ServiceAbgeschlossen';
import EmilogLostDataUpload from './EmilogLostDataUpload';
import EmiconMesstermin from '../../../emicon/components/Einstellungen/EmiconMesstermin';
import KatWechselGeplant from './KatWechselGeplant';
import KatWechselAbgeschlossen from './KatWechselAbgeschlossen';

const useStyles = makeStyles((theme) => ({
  '@global': {
    '.MuiFilledInput-input': {
      padding: '6px 20px 6px 8px',
    },
    '.MuiCardHeader-action': {
      margin: 0,
    },
  },
  content: {
    marginTop: theme.spacing(1),
  },
  container: {
    width: '80vw',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    [theme.breakpoints.down('md')]: {
      padding: 0,
      width: '90vw',
    },
  },
  doubleCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  expandingCard: {
    display: 'flex',
    flexGrow: 1,
  },
}));

export default function Einstellungen() {
  const {
    currentDevice, setCurrentDevice, fetchDevices
  } = useRouting('Service', 'emilog');

  const active = typeof currentDevice?.MAC !== 'undefined';
  const classes = useStyles();

  const [hasWartungPlanned, setHasWartungPlanned] = useState<boolean | null>(null);
  const [lastWartungPlannedDate, setLastWartungPlannedDate] = useState<number | null>(null);

  const [hasServicePlanned, setHasServicePlanned] = useState<boolean | null>(null);
  const [lastServicePlannedDate, setLastServicePlannedDate] = useState<number | null>(null);

  const [hasKatPlanned, setHasKatPlanned] = useState<boolean | null>(null);
  const [lastKatPlannedDate, setLastKatPlannedDate] = useState<number | null>(null);

  async function checkHasActiveWartung() {
    if (currentDevice.serialNumber === null || currentDevice.serialNumber === undefined) {
      return;
    }
    const result = await API.post('emilog', 'hasServicePlanned', {
      body: {
        serialNumber: currentDevice.serialNumber,
        isEmicon: false,
        isWartung: true // ACHTUNG
      },
    });
    setHasWartungPlanned(result.hasWartungPlanned ?? false);
    setLastWartungPlannedDate(result.lastWartungPlannedDate ?? 0);
  }

  async function checkHasActiveService() {
    if (currentDevice.serialNumber === null || currentDevice.serialNumber === undefined) {
      return;
    }
    const result = await API.post('emilog', 'hasServicePlanned', {
      body: {
        serialNumber: currentDevice.serialNumber,
        isEmicon: false,
      },
    });
    setHasServicePlanned(result.hasServicePlanned ?? false);
    setLastServicePlannedDate(result.lastServicePlannedDate ?? 0);
  }

  async function checkHasActiveKatWechsel() {
    if (currentDevice.serialNumber === null || currentDevice.serialNumber === undefined) {
      return;
    }
    const result = await API.post('emilog', 'hasServicePlanned', {
      body: {
        serialNumber: currentDevice.serialNumber,
        isEmicon: false,
        isKat: true // ACHTUNG
      },
    });
    setHasKatPlanned(result.hasKatWechselPlanned ?? false);
    setLastKatPlannedDate(result.lastKatWechselPlannedDate ?? 0);
  }

  useEffect(() => {
    checkHasActiveWartung();
    checkHasActiveService();
    checkHasActiveKatWechsel();
  }, [currentDevice.serialNumber, fetchDevices]); // Experimentel

  // console.log({ currentDevice })

  return (
    <Container className={classes.container} maxWidth="xl">
      <SetupHeader hasBearbeitung={true} />
      <Grid container spacing={2} className={classes.content} justifyContent="center">

        <Grid item lg={6} className={classes.expandingCard}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12}>
              <WartungGeplant
                hasWartungPlanned={hasWartungPlanned}
                lastWartungPlannedDate={lastWartungPlannedDate}
              />
            </Grid>

            <Grid item xs={12}>
              <ServiceGeplant hasServicePlanned={hasServicePlanned} lastServicePlannedDate={lastServicePlannedDate} />
            </Grid>

            <Grid item xs={12}>
              <KatWechselGeplant hasKatPlanned={hasKatPlanned} lastKatPlannedDate={lastKatPlannedDate} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item lg={6} xs={12} className={classes.expandingCard}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12}>
              <WartungAbgeschlossen
                hasWartungPlanned={hasWartungPlanned}
                lastWartungPlannedDate={lastWartungPlannedDate}
                serialNumber={currentDevice?.MAC ?? ''}
                active={active}
              />
            </Grid>
            <Grid item xs={12}>
              <ServiceAbgeschlossen
                hasServicePlanned={hasServicePlanned}
                lastServicePlannedDate={lastServicePlannedDate}
              />
            </Grid>

            <Grid item xs={12}>
              <KatWechselAbgeschlossen hasKatPlanned={hasKatPlanned} lastKatPlannedDate={lastKatPlannedDate} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item lg={6} xs={12} className={classes.expandingCard}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12}>
              <ResetStunden
                active={active}
                serialNumber={currentDevice?.MAC}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item lg={6} xs={12} className={classes.expandingCard}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <Transfer
                serialNumber={currentDevice?.MAC}
                active={active}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} lg={6}>
          <EmiconMesstermin
            serialNumber={currentDevice?.MAC ?? ''}
            isEmicon={false}
            currentDevice={currentDevice}
            setCurrentDevice={setCurrentDevice}
            fetchDevices={fetchDevices}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <DAC />
        </Grid>

        <Grid item xs={12} lg={6} container>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <ResetWerte />
            </Grid>
            <Grid item xs={12} lg={12} style={{ alignSelf: 'flex-end' }}>
              <DeleteDevice />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Messbericht />
        </Grid>

        <Grid item xs={12} lg={6}>
          <VertragsArten
            serialNumber={currentDevice?.MAC ?? ''}
            isEmicon={false}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <VertragsEnde
            serialNumber={currentDevice?.MAC ?? ''}
            isEmicon={false}
          />
        </Grid>

        <Grid item xs={12} lg={12}>
          <VertragsKommentar
            serialNumber={currentDevice?.MAC ?? ''}
            isEmicon={false}
            rows={14}
          />
        </Grid>

        <Grid item xs={12} lg={12}>
          <EmilogLostDataUpload />
        </Grid>

      </Grid>
    </Container>
  );
}
